import * as React from 'react';
import { Popover, PopoverAnchor, PopoverContent } from './Popover';
import { Input, type FieldControlSize } from './Input';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — Combobox (Story 43.2). Text input + suggestion listbox built
 * exclusively on the DS Popover (Radix) and Input — no cmdk, no new deps.
 * Follows the ARIA combobox pattern: role=combobox, aria-expanded,
 * aria-controls, aria-autocomplete=list, aria-activedescendant; ↑/↓ move the
 * active option, Enter selects, Esc closes. Error state is aria-invalid + the
 * FormField inline message — border stays uniform 1px (no colored accent).
 */
export interface ComboboxOption {
  value: string;
  label: string;
  description?: string;
}

export interface ComboboxProps {
  id: string;
  inputValue: string;
  onInputValueChange: (text: string) => void;
  options: ComboboxOption[];
  onSelect: (option: ComboboxOption) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedValue?: string;
  loading?: boolean;
  loadingMessage?: string;
  emptyMessage?: string;
  invalid?: boolean;
  placeholder?: string;
  controlSize?: FieldControlSize;
  disabled?: boolean;
  onClear?: () => void;
  clearLabel?: string;
}

export const Combobox = React.forwardRef<HTMLInputElement, ComboboxProps>(
  (
    {
      id,
      inputValue,
      onInputValueChange,
      options,
      onSelect,
      open,
      onOpenChange,
      selectedValue,
      loading = false,
      loadingMessage = 'Buscando…',
      emptyMessage = 'Nenhum resultado encontrado',
      invalid = false,
      placeholder,
      controlSize,
      disabled = false,
      onClear,
      clearLabel = 'Limpar seleção',
    },
    ref
  ) => {
    const [activeIndex, setActiveIndex] = React.useState(-1);
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const listboxId = `${id}-listbox`;
    const optionId = (index: number) => `${id}-option-${index}`;

    React.useEffect(() => {
      setActiveIndex(open && options.length > 0 ? 0 : -1);
    }, [open, options]);

    React.useEffect(() => {
      if (!open || activeIndex < 0) return;
      document.getElementById(optionId(activeIndex))?.scrollIntoView?.({ block: 'nearest' });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, activeIndex]);

    const select = (option: ComboboxOption) => {
      onSelect(option);
      onOpenChange(false);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!open) {
          onOpenChange(true);
          return;
        }
        if (options.length === 0) return;
        setActiveIndex((current) => {
          const delta = event.key === 'ArrowDown' ? 1 : -1;
          const next = current + delta;
          if (next < 0) return options.length - 1;
          if (next >= options.length) return 0;
          return next;
        });
        return;
      }
      if (event.key === 'Enter') {
        if (open && activeIndex >= 0 && options[activeIndex]) {
          event.preventDefault();
          select(options[activeIndex]);
        }
        return;
      }
      if (event.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };

    const showClear = Boolean(onClear && inputValue && !disabled);

    return (
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverAnchor asChild>
          <div ref={anchorRef} className="relative">
            <Input
              ref={ref}
              id={id}
              role="combobox"
              aria-expanded={open}
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
              aria-invalid={invalid || undefined}
              autoComplete="off"
              spellCheck={false}
              controlSize={controlSize}
              disabled={disabled}
              placeholder={placeholder}
              value={inputValue}
              onChange={(event) => onInputValueChange(event.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => onOpenChange(false)}
              className={cn(showClear && 'pr-10')}
            />
            {showClear ? (
              <button
                type="button"
                aria-label={clearLabel}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-so-sm p-1 text-sober-secondary transition-colors hover:bg-sober-app hover:text-sober-text focus:outline-none focus:ring-2 focus:ring-sober-primary-fg"
                onMouseDown={(event) => event.preventDefault()}
                onClick={onClear}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M3.5 3.5l7 7m0-7l-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            ) : null}
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          className="max-h-64 w-[var(--radix-popover-trigger-width)] overflow-y-auto"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => {
            if (anchorRef.current?.contains(event.target as Node)) event.preventDefault();
          }}
        >
          <div id={listboxId} role="listbox" aria-label={placeholder}>
            {loading ? (
              <div className="flex items-center gap-2.5 px-2.5 py-2 text-so-14 text-sober-secondary">
                <span
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-sober-outline border-t-sober-primary"
                />
                {loadingMessage}
              </div>
            ) : options.length === 0 ? (
              <p className="px-2.5 py-2 text-so-14 text-sober-secondary">{emptyMessage}</p>
            ) : (
              options.map((option, index) => (
                <div
                  key={option.value}
                  id={optionId(index)}
                  role="option"
                  aria-selected={option.value === selectedValue}
                  className={cn(
                    'flex cursor-pointer items-baseline gap-2 rounded-so-sm px-2.5 py-2 text-so-14 text-sober-text transition-colors',
                    index === activeIndex && 'bg-sober-app'
                  )}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => select(option)}
                >
                  <span className="min-w-0 truncate">{option.label}</span>
                  {option.description ? (
                    <span className="shrink-0 text-so-12 text-sober-secondary">{option.description}</span>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);
Combobox.displayName = 'Combobox';
